import { apiDelete, apiGet, apiPost } from './client';

export interface InviteOrAddExternalInput {
  email: string;
  firstName?: string;
  lastName?: string;
}

export type InviteOrAddAction = 'invited' | 'added_to_project' | 'already_member';

export interface InviteOrAddExternalResponse {
  externalUserId: number;
  action: InviteOrAddAction;
}

/**
 * PM-driven kanban invite. Server splits on whether the email maps to an
 * existing active external_user:
 *   - existing → revives/creates an ExternalUserProject binding for THIS
 *     project (response action: 'added_to_project' | 'already_member').
 *   - new → falls through to the admin invite flow (creates user, sends
 *     email, audits) — response action: 'invited'. firstName + lastName are
 *     required only on this branch; the server enforces.
 */
export async function inviteOrAddExternalToProject(
  projectId: number,
  input: InviteOrAddExternalInput,
): Promise<InviteOrAddExternalResponse> {
  const res = await apiPost<{ data: InviteOrAddExternalResponse }>(
    `/projects/${projectId}/external-invites`,
    input,
  );
  return res.data;
}

export interface ExternalUserSuggestion {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
}

/**
 * PM-driven kanban invite typeahead. Server enforces a 2-character minimum
 * on `q`; this helper short-circuits before hitting the network when the
 * query is too short.
 */
export async function suggestExternalUsers(
  projectId: number,
  q: string,
): Promise<ExternalUserSuggestion[]> {
  const trimmed = q.trim();
  if (trimmed.length < 2) return [];
  const params = new URLSearchParams({ q: trimmed });
  const res = await apiGet<{ data: ExternalUserSuggestion[] }>(
    `/projects/${projectId}/external-invites/suggest?${params.toString()}`,
  );
  return res.data;
}

export interface ProjectExternalMember {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
}

export async function listProjectExternalMembers(
  projectId: number,
): Promise<ProjectExternalMember[]> {
  const res = await apiGet<{ data: ProjectExternalMember[] }>(
    `/projects/${projectId}/external-members`,
  );
  return res.data;
}

export interface AssignableExternalMember {
  id: number;
  firstName: string;
  lastName: string;
}

/**
 * MKS-513 — the assignee picker's External tab.
 *
 * Separate from `listProjectExternalMembers` above on purpose: that one is
 * the project-members admin UI's list and is gated admin/PM because it
 * carries `email`. This one is readable by every project member and carries
 * names only — the picker renders nothing else.
 */
export async function listAssignableExternalMembers(
  projectId: number,
): Promise<AssignableExternalMember[]> {
  const res = await apiGet<{ data: AssignableExternalMember[] }>(
    `/projects/${projectId}/assignable-external-members`,
  );
  return res.data;
}

export interface RemoveProjectExternalMembersResult {
  removed: number[];
  notMember: number[];
}

export async function removeProjectExternalMembers(
  projectId: number,
  externalUserIds: number[],
): Promise<RemoveProjectExternalMembersResult> {
  const res = await apiDelete<{ data: RemoveProjectExternalMembersResult }>(
    `/projects/${projectId}/external-members`,
    { externalUserIds },
  );
  return res.data;
}
